import { Button } from '@consigli/shadcn';
import { DocumentPackageSummarySection, Finding } from '@consigli/types';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { FC, useMemo, useState } from 'react';

import { SectionAccordion } from './summary-section-accordion';

interface AccordionItemProps {
  title: string;
  count: number;
  sections: DocumentPackageSummarySection[];
  findingsById: Map<number, Finding>;
  onNavigateToFindings: () => void;
}

export const AccordionItem: FC<AccordionItemProps> = ({
  title,
  count,
  sections,
  findingsById,
  onNavigateToFindings,
}) => {
  const [open, setOpen] = useState(false);
  const hasContent = sections.length > 0;

  // Sort sections alphabetically and move 'Others' to the end so that it is always last
  const sortedSections = useMemo(
    () =>
      [...sections].sort((a, b) => {
        const aIsOthers = a.heading.trim().toLowerCase() === 'others';
        const bIsOthers = b.heading.trim().toLowerCase() === 'others';
        if (aIsOthers !== bIsOthers) return aIsOthers ? 1 : -1;
        return a.heading.localeCompare(b.heading);
      }),
    [sections],
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center w-full px-4 py-3">
        <Button
          variant="ghost"
          className="flex items-center grow min-w-0 text-left h-auto justify-start px-0 disabled:opacity-100 hover:bg-transparent"
          onClick={() => setOpen((v) => !v)}
          disabled={!hasContent}
        >
          <span className="mr-2 text-gray-400 shrink-0 w-4">
            {hasContent && (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
          </span>
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-semibold text-gray-800 truncate">{title}</span>
            <span className="inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
              {count}
            </span>
          </div>
        </Button>
        <Button
          variant="link"
          size="sm"
          onClick={onNavigateToFindings}
          className="flex items-center gap-1.5 ml-4 px-0 text-xs text-gray-600 whitespace-nowrap shrink-0 h-auto"
        >
          View findings
          <ExternalLink size={10} />
        </Button>
      </div>

      {open && hasContent && (
        <div className="border-t border-gray-200">
          <ul className="divide-y divide-gray-200">
            {sortedSections.map((section) => {
              const sectionFindings = section.findingIds
                .map((id) => findingsById.get(id))
                .filter((f): f is Finding => f !== undefined);

              return (
                <SectionAccordion
                  key={section.id}
                  heading={section.heading}
                  body={section.body}
                  findings={sectionFindings}
                  findingCount={section.findingIds.length}
                />
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
